import moment from 'moment';

/**
 * Copyright (c) 2019, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import PropTypes from 'prop-types';

import React from 'react';
import DateDropdown from './DateDropdown';
import Dropdown from './Dropdown';
import Errors from './Errors';

const dateTimeFormat = 'MM/DD/YYYY';

/**
 * A Component that allows a user to select a date
 */
export default class DatePicker extends React.Component {
  static propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.object,
    labelKey: PropTypes.string,
    label: PropTypes.string,
    readOnly: PropTypes.bool,
    disabled: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    showErrors: PropTypes.bool,
    errors: PropTypes.array
  };

  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      inputValue: null
    };

    // Binds class functions to instance
    this.clickCalendar = this.clickCalendar.bind(this);
    this.closeCalendar = this.closeCalendar.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.onDropdownChange = this.onDropdownChange.bind(this);
  }

  clickCalendar() {
    this.setState({ isOpen: !this.state.isOpen });
  }

  closeCalendar() {
    this.setState({ isOpen: false });
  }

  // Catches changes and stores them in interal state
  onInputChange(event) {
    this.setState({ inputValue: event.target.value, isOpen: false });
  }

  onChange(value) {
    if (!moment(value).isValid()) {
      value = new moment() || this.props.value;
    }
    const newDateTime = value
      .clone()
      .years(value.years())
      .dayOfYear(value.dayOfYear())
      .startOf('minute');
    this.props.onChange(newDateTime);
  }

  // Updates state and resets an input temp state
  onDropdownChange(value, hideCalender) {
    const newState = {
      inputValue: value
    };
    this.onChange(value);
    if (hideCalender) newState.isOpen = false;
    this.setState(newState);
  }

  render() {
    const { isOpen, inputValue } = this.state;
    const {
      value,
      label,
      errors,
      showErrors,
      placeholder,
      name,
      readOnly,
      dateLimitInDays
    } = this.props;
    let inputText = '';
    if (value && !Object.keys(value).length) {
      inputText = '';
    } else if (moment.isMoment(value)) {
      inputText = value.format(dateTimeFormat);
    }
    return (
      <div className="slds-form-element slds-is-relative">
        {label && (
          <label className="slds-form-element__label slds-form-element__label--top slds-text-heading--small">
            {label}
          </label>
        )}
        <div
          id="calendarField"
          className="slds-form-element__control slds-input-has-icon slds-input-has-icon--right"
          onClick={this.clickCalendar}
        >
          <svg
            id="calendarIcon"
            className={
              'slds-input__icon slds-icon-text-default' +
              (inputText ? ' datepicker-active' : '')
            }
            aria-hidden="true"
          >
            <use xlinkHref="/assets/icons/utility-sprite/svg/symbols.svg#event" />
          </svg>
          <input
            type="text"
            name={name}
            className="slds-input datepicker-input"
            placeholder={placeholder}
            readOnly={readOnly}
            value={inputText}
            onChange={this.onInputChange}
          />
        </div>
        <Errors errors={errors} showErrors={showErrors} />
        <Dropdown isOpen={isOpen} onClickOutside={this.closeCalendar}>
          <DateDropdown
            dateTime={inputValue}
            onChange={this.onDropdownChange}
            dateLimitInDays={dateLimitInDays}
          />
        </Dropdown>
      </div>
    );
  }
}
